import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Link2, ChevronRight, BookOpen, Heart, Users, Sparkles, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ATTACHMENT_CONTENT = [
  {
    id: 'styles',
    title: 'Anknytningsstilar',
    icon: Link2,
    description: 'Förstå de fyra grundläggande anknytningsstilarna.',
    content: [
      '**Trygg anknytning:** Du känner dig bekväm med närhet och kan hantera separationer. Du litar på andra och känner dig värdig kärlek.',
      '**Ängslig anknytning:** Du oroar dig ofta för relationer, behöver mycket bekräftelse och är rädd att bli övergiven.',
      '**Undvikande anknytning:** Du värdesätter självständighet högt, kan ha svårt med närhet och drar dig undan när det blir för intimt.',
      '**Desorganiserad anknytning:** En blandning av ängslig och undvikande – du längtar efter närhet men är samtidigt rädd för den.',
      'Din anknytningsstil formas i barndomen men kan förändras genom medvetenhet och arbete.',
      'Att känna igen din stil hjälper dig förstå dina reaktioner i relationer.'
    ],
    questions: [
      'Hur reagerar du när din partner drar sig undan?',
      'Känner du dig trygg i att be om hjälp?',
      'Hur hanterar du konflikter – drar du dig undan eller söker du närhet?',
      'Vilken anknytningsstil tror du att du har?'
    ]
  },
  {
    id: 'patterns',
    title: 'Relationsmönster',
    icon: Users,
    description: 'Identifiera återkommande mönster i dina relationer.',
    content: [
      '**Mönster upprepas:** Vi dras ofta till samma typ av personer och situationer tills vi blir medvetna om varför.',
      '**Projektioner:** Vi ser ofta hos andra det vi inte erkänner hos oss själva.',
      '**Bekräftelsebias:** Vi söker partners som bekräftar vår självbild, även om den är negativ.',
      '**Dramat-triangeln:** Offer, Förövare, Räddare – roller vi ofta hamnar i.',
      '**Rädsla för närhet vs rädsla för övergivenhet:** Två sidor av samma mynt.',
      '**Att bryta mönster:** Börja med att identifiera dem utan att döma dig själv.'
    ],
    questions: [
      'Finns det en "typ" du alltid dras till?',
      'Vilken roll tar du ofta i relationer?',
      'Vad fick dina tidigare relationer att ta slut?',
      'Ser du några gemensamma nämnare i dina relationer?'
    ]
  },
  {
    id: 'love-languages',
    title: 'Kärleksspråk',
    icon: Heart,
    description: 'Lär dig hur du ger och tar emot kärlek.',
    content: [
      '**De fem kärleksspråken:**',
      '**1. Bekräftande ord:** Du värdesätter komplimanger, uppmuntran och "jag älskar dig".',
      '**2. Kvalitetstid:** Odelad uppmärksamhet och närvaro är viktigast för dig.',
      '**3. Gåvor:** Genomtänkta presenter visar att någon tänker på dig.',
      '**4. Tjänster:** Handlingar säger mer än ord – hjälp i vardagen visar kärlek.',
      '**5. Fysisk beröring:** Kramar, handhalten och närhet får dig att känna dig älskad.',
      'Ditt kärleksspråk kan vara annorlunda än din partners – lär er varandras!'
    ],
    questions: [
      'Hur visar du kärlek till andra?',
      'Vad får dig att känna dig mest älskad?',
      'Vet din partner vad ditt kärleksspråk är?',
      'Har du frågat din partner om deras kärleksspråk?'
    ]
  },
  {
    id: 'core-wounds',
    title: 'Kärnsår',
    icon: Shield,
    description: 'Förstå dina djupaste emotionella sår.',
    content: [
      '**Kärnsår är djupa övertygelser** om oss själva som formats i barndomen.',
      '**Vanliga kärnsår:**',
      '• "Jag är inte tillräcklig"',
      '• "Jag är inte värd kärlek"',
      '• "Jag måste prestera för att bli älskad"',
      '• "Mina behov är inte viktiga"',
      '• "Jag kommer alltid bli övergiven"',
      '**De aktiveras i relationer** och får oss att reagera starkt på till synes små saker.',
      '**Läkning sker** genom medvetenhet, självmedkänsla och ibland terapi.'
    ],
    questions: [
      'Vad är du mest rädd för i relationer?',
      'Vilka budskap fick du som barn om ditt värde?',
      'När känner du dig som mest sårbar?',
      'Vilka situationer triggar dina starkaste reaktioner?'
    ]
  },
  {
    id: 'healthy-love',
    title: 'Vad är hälsosam kärlek?',
    icon: Sparkles,
    description: 'Kännetecken på en trygg och hälsosam relation.',
    content: [
      '**Ömsesidig respekt:** Ni värderar varandras åsikter, gränser och drömmar.',
      '**Tillit:** Ni litar på varandra utan att behöva kontrollera.',
      '**Kommunikation:** Ni kan prata om allt – även det svåra.',
      '**Självständighet:** Ni har egna liv, intressen och vänner.',
      '**Stöd:** Ni hejar på varandra och finns där i motgångar.',
      '**Konflikthantering:** Ni bråkar ibland men reparerar efteråt.',
      '**Jämlikhet:** Maktbalansen är rättvis – ingen dominerar.',
      '**Glädje:** Ni har roligt tillsammans och skrattar ofta.'
    ],
    questions: [
      'Vilka av dessa finns i din nuvarande relation?',
      'Vilka saknas?',
      'Hur var kärlek i din uppväxtfamilj?',
      'Vad är din definition av hälsosam kärlek?'
    ]
  }
];

export default function AttachmentPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="attachment-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Anknytning & mönster</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till detta innehåll.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = ATTACHMENT_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="attachment-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Anknytning & mönster</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-blue-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-blue-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="attachment-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Anknytning & mönster</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <Link2 className="w-12 h-12 mx-auto text-blue-500" />
          <h2 className="font-heading text-2xl font-semibold">Anknytning & mönster</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Förstå hur din uppväxt format dina relationsmönster och hur du kan skapa tryggare kopplingar.
          </p>
        </div>

        <div className="space-y-3">
          {ATTACHMENT_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
          <p className="text-blue-800 text-sm">
            💡 <strong>Visste du?</strong> Anknytningsstilar är inte livslånga domar – de kan förändras med medvetenhet och arbete.
          </p>
        </div>
      </main>
    </div>
  );
}
